import { Badge, Button, Icon } from '@sgme/ui';
import classNames from 'classnames';
import { RefreshSchedule } from 'components/shared/RefreshSchedule/RefreshSchedule';
import { useAssertedActiveTabId } from 'hooks/use-asserted-active-tab-id';
import { useTranslateForm } from 'hooks/use-translate-payoff';
import { ScheduleModalBlockDefinition } from 'models/metadata/block.definition';
import { useMemo } from 'react';
import Modal from 'react-bootstrap/Modal';
import { ErrorBadge } from 'routes/essential-workspace/components/form/blocks/error-badge';
import { ExportGridField } from 'routes/essential-workspace/components/form/fields/export-grid';
import { GridWithoutPagination } from 'routes/essential-workspace/components/form/fields/grid-without-pagination';
import { MultipleBadgeField } from 'routes/essential-workspace/components/form/fields/multiple-badge';
import { EssentialBlockNode } from 'routes/essential-workspace/state/model';
import { useToggle } from 'usehooks-ts';

export function ScheduleDatesBlockItem({
  id,
  count,
  nodes,
  translationKey,
  errors,
  hideRefreshSchedule,
  exportGridButton,
}: EssentialBlockNode & ScheduleModalBlockDefinition) {
  const tabId = useAssertedActiveTabId();
  const { translateLabel } = useTranslateForm();
  const [opened, toggle] = useToggle(false);

  const hasCount = count > 0;
  const badgeNodes = useMemo(() => nodes.filter(node => node.type === 'MultipleLabelField'), [nodes]);
  const scheduleTableNode = useMemo(() => nodes.find(node => node.type === 'GridWithoutPagination'), [nodes]);

  const translateKey = translationKey ?? id;
  const fieldLabel = translateLabel(translateKey);
  const blockLabel = translateLabel(`${translateKey}.shortName`);

  return (
    <>
      <section className="grid gap-2" data-block-id={id}>
        <header className="flex-x pt-2 g-col-12 gap-2" style={{ height: 32 }}>
          <h2 className="text-start fs-14 text-secondary fw-normal m-0">{blockLabel}</h2>
          <ErrorBadge errors={errors} />
        </header>

        <div className="g-col-12">
          <div className={classNames('card bg-lvl1', { 'card-bordering-primary': hasCount })}>
            <button className="d-flex gap-2 btn btn-link btn-lg text-secondary" disabled={!hasCount} onClick={toggle}>
              <span className="flex-grow-1 text-start">{fieldLabel}</span>
              {hasCount && (
                <Badge purpose="outline" color="secondary" isPill>
                  {count}
                </Badge>
              )}
              <span className="icon icon-sm">keyboard_arrow_right</span>
            </button>
          </div>
        </div>
      </section>

      <Modal show={opened} onHide={toggle} size="xl" scrollable={false}>
        <Modal.Header className="p-0">
          <h3 className="modal-title h4 lh-1 p-4 me-auto">{fieldLabel}</h3>
          <Button
            purpose="flat"
            color="secondary"
            iconPosition="icon-only"
            size="xl"
            className="m-12px"
            data-bs-dismiss="modal"
            aria-label="Close"
            onClick={toggle}
          >
            <Icon icon="clear" />
          </Button>
        </Modal.Header>

        <Modal.Body className="px-4 py-0 flex-y gap-12px">
          <div className="d-flex gap-12px flex-wrap py-1">
            {badgeNodes.map(badgeNode => (
              <MultipleBadgeField key={badgeNode.id} {...badgeNode} />
            ))}
          </div>

          {scheduleTableNode && <GridWithoutPagination {...scheduleTableNode} className="m-0 mt-2" />}
        </Modal.Body>
        <Modal.Footer className="p4">
          {!hideRefreshSchedule && (
            <RefreshSchedule tabId={tabId} className="btn btn-flat-secondary btn-lg btn-icon-end text-secondary" />
          )}
          {exportGridButton && scheduleTableNode && <ExportGridField node={scheduleTableNode} size="lg" />}
          <Button size="lg" onClick={toggle}>
            Done
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
